import React, {useEffect, useState} from 'react';
import {Button, Card, CardBody, CardHeader, Col, Row} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {IJourney} from 'app/shared/model/journey.model';
import {displaySecondsAsHoursMinutes} from 'app/shared/util/date-utils';
import {underscoreToTitleCase} from 'app/shared/util/text-util';
import LocationSelectionModal from './location-selection-modal';
import DriverSelectionModal from './driver-selection-modal';
import VehicleTypeSelectionModal from './vehicle-type-selection-modal';
import FollowupModal, {FollowupFormData} from './followup/followup-modal';
import IncidentReportModal from './incident-report-modal';
import FeedbackForm from './feedback-form';
import {connect} from "react-redux";
import {createEntity, partialUpdateEntity} from "app/entities/journey/journey.reducer";
import {IRootState} from "app/shared/reducers";
import {IAmbulance} from "app/shared/model/ambulance.model";
import {IDriver} from "app/shared/model/driver.model";
import {JourneyStatus} from "app/shared/model/enumerations/journey-status.model";
import {CaseStatus} from "app/shared/model/enumerations/case-status.model";
import {createPaymentForJourney, reset as resetPayment} from "app/entities/payment/payment.reducer";
import {getMMamaSettings} from "app/modules/administration/mmama-settings/mmama-settings.reducer";
import {IMMamaSettings} from "app/shared/model/mmama-settings-model";
import {resetPatientCaseFollowup, savePatientCaseFollowup} from "app/entities/patient-case/patient-case.reducer";
import './journey-info.scss';
import {fromCents} from "app/shared/util/money-utils";
import {decompressImage} from "app/shared/util/image-util";

import {IHealthcareFacility} from 'app/shared/model/healthcare-facility.model';
import {IVillage} from 'app/shared/model/village.model';

interface JourneyInfoProps extends StateProps, DispatchProps {
  journeyEntity: IJourney;
  index: number;
  totalJourneys: number;
}

export enum LocationType {
  VILLAGE = 'VILLAGE',
  HEALTHCARE_FACILITY = 'HEALTHCARE_FACILITY'
}

export enum JourneyDriverType {
  COMMUNITY = 'COMMUNITY',
  AMBULANCE = 'AMBULANCE'
}

export enum PopupType {
  START = 'start',
  END = 'end'
}

const getContactNumbers = (locationEntity?: IHealthcareFacility | IVillage | null): string[] => {
  if (!locationEntity) return [];
  return [
    locationEntity.phoneNumber,
    locationEntity.phone_number_2 || (locationEntity as any).phoneNumber2,
    locationEntity.phone_number_3 || (locationEntity as any).phoneNumber3
  ].filter(Boolean) as string[];
};

// Location card component
const LocationCard = ({
  title,
  location,
  contactNumbers = [],
  contactName,
  locationIcon = 'hospital',
  hasLocation,
  journeyStatus,
  showViewMap = false,
  onUpdateClick
}: {
  title: string;
  location?: string;
  contactNumbers?: string[];
  contactName?: string;
  locationIcon?: 'hospital' | 'home';
  hasLocation: boolean;
  journeyStatus?: JourneyStatus;
  showViewMap?: boolean;
  onUpdateClick: () => void;
}) => {
  return (
    <Card className={`mb-3 ${hasLocation ? '' : 'missing-info-red'}`}>
      <CardBody className="d-flex justify-content-between align-items-center">
        <div>
          <h6 className="text-muted mb-1">{title}</h6>
          <h5 className={hasLocation ? '' : 'text-danger'}>
            <FontAwesomeIcon icon={locationIcon} className="mr-2"/>
            {location || 'Not Selected'}
          </h5>
          {hasLocation && (contactNumbers.length > 0 || contactName) && (
            <div className="text-muted mt-1" style={{ fontSize: '14px' }}>
              {contactName && (
                <div className="mb-1">
                  <FontAwesomeIcon icon="user" className="mr-2"/>
                  <span>{contactName}</span>
                </div>
              )}
              {contactNumbers.length > 0 && (
                <div>
                  <FontAwesomeIcon icon="phone-alt" className="mr-2"/>
                  <span>{contactNumbers.join(', ')}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          {/*
          {showViewMap && hasLocation && <Button color="link">VIEW MAP</Button>}
          */}
          {journeyStatus !== JourneyStatus.COMPLETE && journeyStatus !== JourneyStatus.CANCELED &&
            <Button color="link" onClick={onUpdateClick}>UPDATE</Button>
          }
        </div>
      </CardBody>
    </Card>
  );
};

// Driver card component
const DriverCard = ({driver, ambulance, hasDriver, onUpdateClick, journeyStatus}) => {
  const driverName = driver
    ? `${driver.firstName} ${driver.lastName}`
    : ambulance
      ? `${ambulance.driverFirstName} ${ambulance.driverLastName}`
      : 'Not Selected';
  const vehicleType = driver?.vehicleType || ambulance?.vehicleType;

  return (
    <Card className={`mb-3 ${hasDriver ? '' : 'missing-info-red'}`}>
      <CardBody className="d-flex justify-content-between align-items-center">
        <div>
          <h6 className="text-muted mb-1">Driver/Ambulance</h6>
          <h5 className={hasDriver ? '' : 'text-danger'}>
            {vehicleType?.image ?
              <img src={`data:image/svg+xml;base64,${decompressImage(vehicleType.image)}`} alt="vehicle_image"
                   width={40} height={40} className="mr-2"/>
              :
              <FontAwesomeIcon icon="car" className="mr-2"/>
            }
            {driverName}
          </h5>
        </div>
        {journeyStatus !== JourneyStatus.COMPLETE && journeyStatus !== JourneyStatus.CANCELED &&
          <Button color="link" onClick={onUpdateClick}>UPDATE</Button>
        }
      </CardBody>
    </Card>
  );
};

// Vehicle Type card component
const VehicleTypeCard = ({vehicleType, hasVehicleType, onUpdateClick, journeyStatus}) => {
  const vehicleTypeName = vehicleType ? vehicleType.enType : 'Not Selected';

  return (
    <Card className={`mb-3 ${hasVehicleType ? '' : 'missing-info-red'}`}>
      <CardBody className="d-flex justify-content-between align-items-center">
        <div>
          <h6 className="text-muted mb-1">Vehicle Type</h6>
          <h5 className={hasVehicleType ? '' : 'text-danger'}>
            {vehicleType?.image ?
              <img src={`data:image/svg+xml;base64,${decompressImage(vehicleType?.image || '')}`} alt="vehicle_image"
                   width={40} height={40} className="mr-2"/>
              :
              <FontAwesomeIcon icon="car" className="mr-2"/>
            }
            {vehicleTypeName}
          </h5>
        </div>
        {journeyStatus !== JourneyStatus.COMPLETE && journeyStatus !== JourneyStatus.CANCELED &&
          <Button color="link" onClick={onUpdateClick}>UPDATE</Button>
        }
      </CardBody>
    </Card>
  );
};

// Incident Report card component
const IncidentReportCard = ({journey, onUpdateClick, onCompleteClick}) => {
  if (!journey.incidentReported || !journey.incidentReport) {
    return null;
  }

  return (
    <Card className="mb-3 incident-report-card border-warning">
      <CardBody>
        <Row className="align-items-center">
          <Col xs="6">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="text-warning mb-0">
                <FontAwesomeIcon icon="exclamation-triangle" className="mr-2"/>
                Incident Reported
              </h5>
            </div>
            <div className="incident-details">
              <strong>Incident:</strong> {journey.incidentReport.enIncident}
              {journey.incidentReportDescription && (
                <div className="mt-2">
                  <strong>Description:</strong> {journey.incidentReportDescription}
                </div>
              )}
            </div>
          </Col>
          <Col xs="6" className="text-right">
            {journey.status !== JourneyStatus.COMPLETE && journey.status !== JourneyStatus.CANCELED && (
              <>
                <Button color="link" onClick={onCompleteClick} className="complete-link">
                  COMPLETE JOURNEY
                </Button>
                <Button color="link" onClick={onUpdateClick}>
                  UPDATE
                </Button>
              </>
            )}
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const JourneyInfoComponent = (props: JourneyInfoProps) => {
  const {
    journeyEntity,
    index,
    totalJourneys,
    updating,
    creatingPayment,
    mmamaSettings,
  } = props;
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [destinationModalOpen, setDestinationModalOpen] = useState(false);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [vehicleTypeModalOpen, setVehicleTypeModalOpen] = useState(false);
  const [followupModalOpen, setFollowupModalOpen] = useState(false);
  const [incidentReportModalOpen, setIncidentReportModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [isCancelJourney, setIsCancelJourney] = useState(false);
  const [countrySettings, setCountrySettings] = useState<IMMamaSettings | null>(null);

  const patientCase = journeyEntity.patientCase;
  const patient = patientCase?.patient;
  const isBypass = Boolean(patientCase?.bypass);
  const pickupLocation = journeyEntity.startHealthcareFacility || journeyEntity.startVillage;
  const destinationLocation = journeyEntity.endHealthcareFacility || journeyEntity.endVillage;
  const hasPickupLocation = Boolean(pickupLocation);
  const hasDestination = Boolean(destinationLocation);
  const hasDriver = Boolean(journeyEntity.driver || journeyEntity.ambulance);
  const hasVehicleType = Boolean(journeyEntity.vehicleType);
  const pickupLocationName = pickupLocation?.name;
  const destinationName = destinationLocation?.name;
  const pickupContactNumbers = getContactNumbers(pickupLocation);
  const pickupContactName = pickupLocation?.contactName;
  const destinationContactNumbers = getContactNumbers(destinationLocation);
  const destinationContactName = destinationLocation?.contactName;
  const showViewMap = hasPickupLocation && hasDestination;

  const togglePickupModal = () => setPickupModalOpen(!pickupModalOpen);
  const toggleDestinationModal = () => setDestinationModalOpen(!destinationModalOpen);
  const toggleDriverModal = () => setDriverModalOpen(!driverModalOpen);
  const toggleVehicleTypeModal = () => setVehicleTypeModalOpen(!vehicleTypeModalOpen);
  const toggleFollowupModal = () => setFollowupModalOpen(!followupModalOpen);
  const toggleFeedbackModal = () => setFeedbackModalOpen(!feedbackModalOpen);
  const toggleIncidentReportModal = (isCancel = false) => {
    setIsCancelJourney(isCancel);
    setIncidentReportModalOpen(!incidentReportModalOpen);
  };
  const patientCountryId = journeyEntity?.patientCase?.patient?.countryId;

  const handlePickupSave = (locationType: LocationType, locationId: string) => {
    props.partialUpdateEntity(journeyEntity.id, {
      [locationType === LocationType.VILLAGE ? "startVillageId" : "startHealthcareId"]: locationId,
      status: JourneyStatus.NEEDS_DESTINATION,
    });
  };

  const handleDestinationSave = (locationType: LocationType, locationId: string, totalCost?: number, totalDistance?: number, totalDurationSeconds?: number, routeId?: string, geofenceRoute?: boolean) => {
    props.partialUpdateEntity(journeyEntity.id, {
      [locationType === LocationType.VILLAGE ? "endVillageId" : "endHealthcareId"]: locationId,
      status: isBypass ? JourneyStatus.NEEDS_VEHICLE_TYPE : JourneyStatus.NEEDS_DRIVER,
      ...(totalCost && {totalCost}),
      ...(totalDistance && {totalDistance}),
      ...(totalDurationSeconds && {totalDurationSeconds}),
      ...(routeId && {routeId}),
      ...(geofenceRoute !== undefined && {geofenceRoute}),
    });
  };

  const handleDriverSave = (driverType: JourneyDriverType, selectedDriver: IAmbulance | IDriver, totalDistance: number, totalDurationSeconds: number, totalCost: number) => {
    props.partialUpdateEntity(journeyEntity.id, {
      [driverType === JourneyDriverType.AMBULANCE ? "ambulanceId" : "driverId"]: selectedDriver.id,
      status: JourneyStatus.AWAITING_PICKUP,
      totalDistance,
      totalDurationSeconds,
      totalCost
    });
  };

  const handleVehicleTypeSave = (vehicleTypeId: string) => {
    props.partialUpdateEntity(journeyEntity.id, {
      vehicleTypeId,
      status: JourneyStatus.COMPLETE,
    });
  };

  const handleStatusSave = (newStatus: JourneyStatus) => {
    props.partialUpdateEntity(journeyEntity.id, {
      status: newStatus,
    });
  }

  const handlePickupSuccessful = () => {
    if (journeyEntity.status === JourneyStatus.AWAITING_PICKUP) {
      handleStatusSave(JourneyStatus.AWAITING_DROP_OFF);
    }
  };

  const handleDropOffSuccessful = () => {
    if (journeyEntity.status === JourneyStatus.AWAITING_DROP_OFF) {
      if (window.confirm(`This action can not be undone. Make sure the driver has dropped off the patient, ${patient?.firstName} ${patient?.lastName}, at the ${journeyEntity.endHealthcareFacility?.name} before confirming drop off.`)) {
        handleStatusSave(JourneyStatus.COMPLETE);
      }
    }
  };

  const handleCancellation = () => {
    toggleIncidentReportModal(true);
  };

  const handleFollowupSave = (followupData: FollowupFormData) => {
    props.savePatientCaseFollowup(followupData);
  };

  const handleIncidentSave = (incidentId: string, incidentDescription: string, isCancel: boolean) => {
    const updateData: Partial<IJourney> = {
      incidentReported: true,
      incidentReportId: incidentId,
      incidentReportDescription: incidentDescription,
    };

    // If this is a cancellation, also update the status
    if (isCancel) {
      updateData.status = JourneyStatus.CANCELED;
    }

    props.partialUpdateEntity(journeyEntity.id, updateData);
  };

  const addJourney = (locationType: LocationType = null, locationId: string = null) => {
    const newJourney: IJourney = {
      startVillageId: locationType === LocationType.VILLAGE ? locationId : null,
      startHealthcareFacilityId: locationType === LocationType.HEALTHCARE_FACILITY ? locationId : null,
      patientCaseId: journeyEntity.patientCase?.id,
    };
    props.createEntity(newJourney);
  };

  const handleAddJourney = () => {
    // Confirm if the user wants to add a new journey
    if (!window.confirm(`Are you sure you want to add a new journey?`)) {
      return;
    }
    addJourney();
  }

  const handleOnwardTransfer = () => {
    // Use the drop-off location as the new pickup location for onward transfer
    const onwardPickupLocationType = journeyEntity.endHealthcareFacility ? LocationType.HEALTHCARE_FACILITY : LocationType.VILLAGE;
    const onwardPickupLocation = journeyEntity.endHealthcareFacility || journeyEntity.endVillage;

    if (onwardPickupLocationType === LocationType.VILLAGE && !countrySettings?.allowVillagePickup) {
      alert('Village pickup is not allowed by current settings.');
      return;
    }

    if (onwardPickupLocationType && onwardPickupLocation) {
      // Confirm if the user wants to transfer to a new journey
      if (!window.confirm(`Are you sure you want to transfer to a new journey from ${onwardPickupLocation.name}?`)) {
        return;
      }

      addJourney(
        onwardPickupLocationType,
        onwardPickupLocation.id
      );
    } else {
      alert('No drop-off location available for onward transfer.');
    }
  }

  useEffect(() => {
    if (!props.mmamaSettings || props.mmamaSettings.length === 0) {
      props.getMMamaSettings();
    }
  }, [props.mmamaSettings, props.getMMamaSettings]);

  useEffect(() => {
    if (mmamaSettings && mmamaSettings.length > 0 && patientCountryId) {
      const settings = mmamaSettings.find(setting => setting.country.id === patientCountryId);
      if (settings) {
        setCountrySettings(settings);
      }
    }
  }, [mmamaSettings, patientCountryId]);

  return (
    <>
      <Card className="mb-3">
        <CardHeader className="bg-white">
          <Row className="align-items-center mb-2">
            <Col md={4} className="d-flex align-items-center">
              <h4 className="mb-0">
                Journey#{index} <span
                className="text-primary"
                style={{fontSize: '20px'}}>{underscoreToTitleCase(journeyEntity.status || '')}</span>
              </h4>
            </Col>
            <Col md={8}>
              <Row className="align-items-center">
                {(() => {

                  const paymentRequired =
                    (journeyEntity.driver?.id && journeyEntity.driver.vehicleType?.paymentRequired)
                    || (journeyEntity.ambulance?.id && journeyEntity.ambulance.vehicleType?.paymentRequired);

                  const showPaymentButton =
                    (patientCase.caseStatus === CaseStatus.READY_FOR_PAYMENT && !journeyEntity.payment?.id && paymentRequired)
                    || journeyEntity.payment?.id

                  const routeInfo = (
                    <div className="text-secondary route-info">
                      <div className="d-flex flex-wrap justify-content-end">
                        {paymentRequired && <span className={showPaymentButton ? "mr-3" : "mr-4"}>
                          <strong>Total Cost:</strong> <span
                          className="text-primary">~{fromCents(journeyEntity.totalCost)} {countrySettings?.currencySymbol}</span>
                        </span>
                        }
                        <span className={showPaymentButton ? "mr-3" : "mr-4"}>
                          <strong>Route:</strong> <span
                          className="text-primary">~{journeyEntity.totalDistance?.toFixed(2)}km</span>
                        </span>
                        <span className={showPaymentButton ? "mr-3" : "mr-4"}>
                          <strong>Time:</strong> <span
                          className="text-primary">~{journeyEntity.totalDurationSeconds ? displaySecondsAsHoursMinutes(journeyEntity.totalDurationSeconds) : ''}</span>
                        </span>
                      </div>
                    </div>
                  );

                  if (showPaymentButton) {
                    return (
                      <>
                        <Col md={4}>
                          {patientCase.caseStatus === CaseStatus.READY_FOR_PAYMENT && !journeyEntity.payment?.id
                            ? <Button
                              color="success"
                              onClick={() => props.createPaymentForJourney(journeyEntity.id, journeyEntity.totalCost)}
                              disabled={creatingPayment}
                              size="sm"
                              style={{fontSize: '14px', textDecoration: 'underline'}}
                            >
                              REQUEST PAYMENT
                            </Button>
                            : <Button disabled color="danger" size="sm" style={{fontSize: '13px'}}>
                              <FontAwesomeIcon icon="money-check" className="mr-1"/>PAYMENT REQUESTED
                            </Button>
                          }
                        </Col>
                        <Col md={8} className="d-flex align-items-center justify-content-end">
                          {routeInfo}
                        </Col>
                      </>
                    );
                  }

                  return (
                    <Col md={12} className="d-flex align-items-center justify-content-end">
                      {routeInfo}
                    </Col>
                  );
                })()}
              </Row>
            </Col>
          </Row>
        </CardHeader>
        <CardBody>
          <LocationCard
            title="Pickup Location"
            location={pickupLocationName}
            contactNumbers={pickupContactNumbers}
            contactName={pickupContactName}
            locationIcon={journeyEntity.startVillage ? 'home' : 'hospital'}
            hasLocation={hasPickupLocation}
            onUpdateClick={togglePickupModal}
            journeyStatus={journeyEntity.status}
          />

          <LocationCard
            title="Destination"
            location={destinationName}
            contactNumbers={destinationContactNumbers}
            contactName={destinationContactName}
            locationIcon={journeyEntity.endVillage ? 'home' : 'hospital'}
            hasLocation={hasDestination}
            showViewMap={showViewMap}
            onUpdateClick={toggleDestinationModal}
            journeyStatus={journeyEntity.status}
          />

          {isBypass ? (
            <VehicleTypeCard
              vehicleType={journeyEntity.vehicleType}
              hasVehicleType={hasVehicleType}
              onUpdateClick={toggleVehicleTypeModal}
              journeyStatus={journeyEntity.status}
            />
          ) : (
            <DriverCard
              driver={journeyEntity.driver}
              ambulance={journeyEntity.ambulance}
              hasDriver={hasDriver}
              onUpdateClick={toggleDriverModal}
              journeyStatus={journeyEntity.status}
            />
          )}

          <IncidentReportCard
            journey={journeyEntity}
            onUpdateClick={() => toggleIncidentReportModal(false)}
            onCompleteClick={() => handleStatusSave(JourneyStatus.COMPLETE)}
          />

          {index === totalJourneys && (
            <Row>
              {journeyEntity.status === JourneyStatus.COMPLETE && !isBypass &&
                <Col md="4">
                  <Button color="secondary" className="btn-block mt-3 py-2 font-weight-bold"
                          onClick={handleOnwardTransfer} disabled={updating}>
                    Onward Transfer
                  </Button>
                </Col>
              }
              {(journeyEntity.status === JourneyStatus.AWAITING_PICKUP || journeyEntity.status === JourneyStatus.AWAITING_DROP_OFF) &&
                <Col md="4">
                  <Button color="warning" className="btn-block mt-3 py-2 font-weight-bold"
                          onClick={() => toggleIncidentReportModal(false)} disabled={updating}>
                    REPORT INCIDENT
                  </Button>
                </Col>
              }
              {journeyEntity.status !== JourneyStatus.CANCELED && journeyEntity.status !== JourneyStatus.COMPLETE &&
                <Col md="4">
                  <Button color="danger" className="btn-block mt-3 py-2 font-weight-bold"
                          onClick={handleCancellation} disabled={updating}>
                    CANCEL JOURNEY
                  </Button>
                </Col>
              }

              {journeyEntity.status === JourneyStatus.COMPLETE && !isBypass &&
                <Col md="4">
                  <Button color="secondary" className="btn-block mt-3 py-2 font-weight-bold"
                          onClick={handleAddJourney} disabled={updating}>
                    Add Journey
                  </Button>
                </Col>
              }
              {journeyEntity.status === JourneyStatus.AWAITING_PICKUP &&
                <Col md="4">
                  <Button color="primary" className="btn-block mt-3 py-2 font-weight-bold"
                          onClick={handlePickupSuccessful} disabled={updating}>
                    CONFIRM PICKUP
                  </Button>
                </Col>
              }
              {journeyEntity.status === JourneyStatus.AWAITING_DROP_OFF &&
                <Col md="4">
                  <Button color="primary" className="btn-block mt-3 py-2 font-weight-bold"
                          onClick={handleDropOffSuccessful} disabled={updating}>
                    CONFIRM DROP-OFF
                  </Button>
                </Col>
              }

              {/* Empty columns for spacing for Bypass Completed Journeys */}
              {journeyEntity.status === JourneyStatus.COMPLETE && isBypass &&
                <>
                  <Col md="4"></Col>
                  <Col md="4"></Col>
                </>
              }
              {journeyEntity.status === JourneyStatus.COMPLETE && patientCase?.caseStatus === CaseStatus.FOLLOW_UP &&
                <Col md="4">
                  <Button color="primary" className="btn-block mt-3 py-2 font-weight-bold"
                          onClick={toggleFollowupModal} disabled={updating}>
                    FOLLOW UP
                  </Button>
                </Col>
              }
              {/* Feedback Button */}
              {(patientCase?.caseStatus === CaseStatus.PENDING_PAYMENT || patientCase?.caseStatus === CaseStatus.CLOSED) &&
                countrySettings?.enableDispatcherFeedback &&
                <Col md="4">
                  <Button
                    color="info"
                    className="btn-block mt-3 py-2 font-weight-bold"
                    onClick={toggleFeedbackModal}
                  >
                    {patientCase?.patientFeedbackId ? 'FEEDBACK COMPLETE' : 'FEEDBACK'}
                  </Button>
                </Col>
              }
            </Row>
          )}
        </CardBody>
      </Card>

      {/* Location Selection Modals */}
      <LocationSelectionModal
        isOpen={pickupModalOpen}
        toggle={togglePickupModal}
        onSave={handlePickupSave}
        journey={journeyEntity}
        popupType={PopupType.START}
      />

      <LocationSelectionModal
        isOpen={destinationModalOpen}
        toggle={toggleDestinationModal}
        onSave={handleDestinationSave}
        journey={journeyEntity}
        popupType={PopupType.END}
      />

      {/* Driver Selection Modal */}
      <DriverSelectionModal
        isOpen={driverModalOpen}
        toggle={toggleDriverModal}
        onSave={handleDriverSave}
        journey={journeyEntity}
        fromLocation={pickupLocationName}
        toLocation={destinationName}
      />

      {/* Vehicle Type Selection Modal */}
      <VehicleTypeSelectionModal
        isOpen={vehicleTypeModalOpen}
        toggle={toggleVehicleTypeModal}
        onSave={handleVehicleTypeSave}
        journey={journeyEntity}
      />

      {/* Followup Modal */}
      <FollowupModal
        isOpen={followupModalOpen}
        toggle={toggleFollowupModal}
        journey={journeyEntity}
        countrySettings={countrySettings}
        onSave={handleFollowupSave}
      />

      {/* Incident Report Modal */}
      <IncidentReportModal
        isOpen={incidentReportModalOpen}
        toggle={() => toggleIncidentReportModal(false)}
        journey={journeyEntity}
        isCancel={isCancelJourney}
        onSave={handleIncidentSave}
      />

      {/* Feedback Modal */}
      <FeedbackForm
        isOpen={feedbackModalOpen}
        patientCase={journeyEntity.patientCase}
        journey={journeyEntity}
        onClose={toggleFeedbackModal}
        countrySettings={countrySettings}
      />
    </>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  updating: storeState.journey.updating,
  creatingPayment: storeState.payment.updating,
  mmamaSettings: storeState.mmamaSettings.settings,
});

const mapDispatchToProps = {
  getMMamaSettings,
  partialUpdateEntity,
  createEntity,
  createPaymentForJourney,
  resetPayment,
  savePatientCaseFollowup,
  resetPatientCaseFollowup
};

export type StateProps = ReturnType<typeof mapStateToProps>;
export type DispatchProps = typeof mapDispatchToProps;

const JourneyInfo = connect(mapStateToProps, mapDispatchToProps)(JourneyInfoComponent);

export default JourneyInfo;
